// TanStack Query hooks for the Compliance / company-licensing module (Plan 6).
// Tracks Darkspire Risk LLC's OWN regulatory standing per US jurisdiction:
// the catalog (jurisdictions / regulators / license types / requirements /
// reciprocity / training certs) and the company-state half (jurisdiction
// targets, qualifying agents, company licenses + their M:N insurance/bond
// dependency links, insurance policies/certificates, surety bonds), plus the
// gap-analysis and renewal-calendar analytics.
//
// Mirrors packages/api/src/routes/compliance.ts (company-state) and
// compliance-catalog.ts (catalog), both mounted at /api/compliance. House
// pattern follows api/queries/persons.ts: thin api.* fetch wrappers, a single
// keys factory for invalidation, query params built off URLSearchParams.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  ComplianceCalendarResponse,
  ComplianceRosterResponse,
  ComplianceLicenseTypeResponse,
  ComplianceLicenseTypesListResponse,
  ComplianceRequirementResponse,
  ComplianceRequirementsListResponse,
  ComplianceTrainingCertResponse,
  ComplianceTrainingCertsListResponse,
  CompanyLicenseResponse,
  CompanyLicensesListResponse,
  CreateComplianceLicenseTypeInput,
  CreateComplianceRequirementInput,
  CreateComplianceTrainingCertInput,
  CreateCompanyLicenseInput,
  CreateInsuranceCertificateInput,
  CreateInsurancePolicyInput,
  CreateJurisdictionTargetInput,
  CreateQualifyingAgentInput,
  CreateReciprocityAgreementInput,
  CreateSuretyBondInput,
  GapAnalysisResponse,
  InsuranceCertificateResponse,
  InsuranceCertificatesListResponse,
  InsurancePoliciesListResponse,
  InsurancePolicyResponse,
  JurisdictionTargetResponse,
  JurisdictionTargetsListResponse,
  JurisdictionsListResponse,
  QualifyingAgentResponse,
  QualifyingAgentsListResponse,
  ReciprocityAgreementResponse,
  ReciprocityAgreementsListResponse,
  RegulatorsListResponse,
  SuretyBondResponse,
  SuretyBondsListResponse,
  UpdateComplianceLicenseTypeInput,
  UpdateComplianceRequirementInput,
  UpdateComplianceTrainingCertInput,
  UpdateCompanyLicenseInput,
  UpdateInsurancePolicyInput,
  UpdateJurisdictionTargetInput,
  UpdateQualifyingAgentInput,
  UpdateReciprocityAgreementInput,
  UpdateSuretyBondInput,
} from '@dsr/shared-types';
import { api, uploadFile, downloadBlob } from '../client';

// ---------- Query keys ----------

const KEY = 'compliance';

export const complianceKeys = {
  all: [KEY] as const,
  // Analytics
  gap: () => [KEY, 'gap-analysis'] as const,
  calendar: () => [KEY, 'calendar'] as const,
  roster: () => [KEY, 'roster'] as const,
  // A collection subtree per resource segment; list(seg, filters) hangs off it
  // so a single invalidate of coll(seg) refreshes every filtered list.
  coll: (seg: string) => [KEY, seg] as const,
  list: (seg: string, filters?: object) =>
    [KEY, seg, 'list', { ...(filters ?? {}) }] as const,
  detail: (seg: string, id: number) => [KEY, seg, 'detail', id] as const,
};

// Build a query string from a params object, skipping undefined / empty.
function qs(params?: object): string {
  if (!params) return '';
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(params as Record<string, unknown>)) {
    if (v === undefined || v === null || v === '') continue;
    sp.set(k, String(v));
  }
  const s = sp.toString();
  return s.length > 0 ? `?${s}` : '';
}

function url(seg: string, params?: object): string {
  return `/api/compliance/${seg}${qs(params)}`;
}

// =============================================================================
// Analytics
// =============================================================================

export function useComplianceGap() {
  return useQuery({
    queryKey: complianceKeys.gap(),
    queryFn: ({ signal }) =>
      api.get<GapAnalysisResponse>('/api/compliance/gap-analysis', signal),
  });
}

export function useComplianceCalendar() {
  return useQuery({
    queryKey: complianceKeys.calendar(),
    queryFn: ({ signal }) =>
      api.get<ComplianceCalendarResponse>('/api/compliance/calendar', signal),
  });
}

export function useComplianceRoster(enabled = true) {
  return useQuery({
    queryKey: complianceKeys.roster(),
    queryFn: ({ signal }) =>
      api.get<ComplianceRosterResponse>('/api/compliance/roster', signal),
    enabled,
  });
}

// =============================================================================
// Catalog — filterable lists (read-only here for the param'd queries)
// =============================================================================

export interface JurisdictionsFilters { kind?: string; q?: string }
export function useJurisdictions(params?: JurisdictionsFilters) {
  return useQuery({
    queryKey: complianceKeys.list('jurisdictions', params),
    queryFn: ({ signal }) =>
      api.get<JurisdictionsListResponse>(url('jurisdictions', params), signal),
  });
}

export interface RegulatorsFilters { jurisdiction_id?: number }
export function useRegulators(params?: RegulatorsFilters) {
  return useQuery({
    queryKey: complianceKeys.list('regulators', params),
    queryFn: ({ signal }) =>
      api.get<RegulatorsListResponse>(url('regulators', params), signal),
  });
}

export interface LicenseTypesFilters {
  jurisdiction_id?: number;
  scope?: string;
  service_line?: string;
}
export function useLicenseTypes(params?: LicenseTypesFilters) {
  return useQuery({
    queryKey: complianceKeys.list('license-types', params),
    queryFn: ({ signal }) =>
      api.get<ComplianceLicenseTypesListResponse>(url('license-types', params), signal),
  });
}

export interface RequirementsFilters {
  jurisdiction_id?: number;
  service_line?: string;
  kind?: string;
  severity?: string;
}
export function useRequirements(params?: RequirementsFilters) {
  return useQuery({
    queryKey: complianceKeys.list('requirements', params),
    queryFn: ({ signal }) =>
      api.get<ComplianceRequirementsListResponse>(url('requirements', params), signal),
  });
}

export interface ReciprocityFilters {
  home_jurisdiction_id?: number;
  host_jurisdiction_id?: number;
  service_line?: string;
  status?: string;
}
export function useReciprocity(params?: ReciprocityFilters) {
  return useQuery({
    queryKey: complianceKeys.list('reciprocity', params),
    queryFn: ({ signal }) =>
      api.get<ReciprocityAgreementsListResponse>(url('reciprocity', params), signal),
  });
}

export interface TrainingCertsFilters { jurisdiction_id?: number; kind?: string }
export function useTrainingCerts(params?: TrainingCertsFilters) {
  return useQuery({
    queryKey: complianceKeys.list('training-certs', params),
    queryFn: ({ signal }) =>
      api.get<ComplianceTrainingCertsListResponse>(url('training-certs', params), signal),
  });
}

// =============================================================================
// Company-state lists / detail
// =============================================================================

export function useTargets() {
  return useQuery({
    queryKey: complianceKeys.list('targets'),
    queryFn: ({ signal }) =>
      api.get<JurisdictionTargetsListResponse>(url('targets'), signal),
  });
}

export function useQualifyingAgents() {
  return useQuery({
    queryKey: complianceKeys.list('qualifying-agents'),
    queryFn: ({ signal }) =>
      api.get<QualifyingAgentsListResponse>(url('qualifying-agents'), signal),
  });
}

export interface CompanyLicenseFilters {
  status?: string;
  jurisdiction_id?: number;
  service_line?: string;
}
/** @deprecated alias kept for older call sites; prefer CompanyLicenseFilters. */
export type CompanyLicensesFilters = CompanyLicenseFilters;
export function useCompanyLicenses(params?: CompanyLicenseFilters) {
  return useQuery({
    queryKey: complianceKeys.list('licenses', params),
    queryFn: ({ signal }) =>
      api.get<CompanyLicensesListResponse>(url('licenses', params), signal),
  });
}

export function useCompanyLicense(id: number | null) {
  return useQuery({
    queryKey: complianceKeys.detail('licenses', id ?? 0),
    enabled: id !== null && id > 0,
    queryFn: ({ signal }) =>
      api.get<CompanyLicenseResponse>(`/api/compliance/licenses/${id}`, signal),
  });
}

export function useInsurancePolicies() {
  return useQuery({
    queryKey: complianceKeys.list('insurance-policies'),
    queryFn: ({ signal }) =>
      api.get<InsurancePoliciesListResponse>(url('insurance-policies'), signal),
  });
}

export function useInsuranceCertificates() {
  return useQuery({
    queryKey: complianceKeys.list('insurance-certificates'),
    queryFn: ({ signal }) =>
      api.get<InsuranceCertificatesListResponse>(url('insurance-certificates'), signal),
  });
}

export function useBonds() {
  return useQuery({
    queryKey: complianceKeys.list('bonds'),
    queryFn: ({ signal }) =>
      api.get<SuretyBondsListResponse>(url('bonds'), signal),
  });
}

// =============================================================================
// Mutations
//   Writes invalidate the affected collection subtree (and gap/calendar, which
//   are derived from these rows). Update/Delete variants take an { id } arg
//   alongside the patch so the page passes a single object.
// =============================================================================

// Invalidate a collection subtree plus the derived analytics queries.
function invalidateColl(
  qc: ReturnType<typeof useQueryClient>,
  ...segs: string[]
): void {
  for (const seg of segs) {
    void qc.invalidateQueries({ queryKey: complianceKeys.coll(seg) });
  }
  void qc.invalidateQueries({ queryKey: complianceKeys.gap() });
  void qc.invalidateQueries({ queryKey: complianceKeys.calendar() });
}

// ---------- Targets ----------

export function useCreateTarget() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateJurisdictionTargetInput) =>
      api.post<JurisdictionTargetResponse>('/api/compliance/targets', input),
    onSuccess: () => invalidateColl(qc, 'targets'),
  });
}

export interface UpdateTargetArgs extends UpdateJurisdictionTargetInput { id: number }
export function useUpdateTarget() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...patch }: UpdateTargetArgs) =>
      api.patch<JurisdictionTargetResponse>(`/api/compliance/targets/${id}`, patch),
    onSuccess: () => invalidateColl(qc, 'targets'),
  });
}

export function useDeleteTarget() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.delete<void>(`/api/compliance/targets/${id}`),
    onSuccess: () => invalidateColl(qc, 'targets'),
  });
}

// ---------- Qualifying agents ----------

export function useCreateQualifyingAgent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateQualifyingAgentInput) =>
      api.post<QualifyingAgentResponse>('/api/compliance/qualifying-agents', input),
    onSuccess: () => invalidateColl(qc, 'qualifying-agents'),
  });
}

export interface UpdateQualifyingAgentArgs extends UpdateQualifyingAgentInput { id: number }
export function useUpdateQualifyingAgent() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...patch }: UpdateQualifyingAgentArgs) =>
      api.patch<QualifyingAgentResponse>(`/api/compliance/qualifying-agents/${id}`, patch),
    onSuccess: () => invalidateColl(qc, 'qualifying-agents'),
  });
}

// ---------- Company licenses ----------

export function useCreateCompanyLicense() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateCompanyLicenseInput) =>
      api.post<CompanyLicenseResponse>('/api/compliance/licenses', input),
    onSuccess: () => invalidateColl(qc, 'licenses'),
  });
}

export interface UpdateCompanyLicenseArgs extends UpdateCompanyLicenseInput { id: number }
export function useUpdateCompanyLicense() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...patch }: UpdateCompanyLicenseArgs) =>
      api.patch<CompanyLicenseResponse>(`/api/compliance/licenses/${id}`, patch),
    onSuccess: (_data, { id }) => {
      invalidateColl(qc, 'licenses');
      void qc.invalidateQueries({ queryKey: complianceKeys.detail('licenses', id) });
    },
  });
}

export function useDeleteCompanyLicense() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => api.delete<void>(`/api/compliance/licenses/${id}`),
    onSuccess: () => invalidateColl(qc, 'licenses'),
  });
}

// ---------- Insurance policies ----------

export function useCreateInsurancePolicy() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateInsurancePolicyInput) =>
      api.post<InsurancePolicyResponse>('/api/compliance/insurance-policies', input),
    onSuccess: () => invalidateColl(qc, 'insurance-policies'),
  });
}

export interface UpdateInsurancePolicyArgs extends UpdateInsurancePolicyInput { id: number }
export function useUpdateInsurancePolicy() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...patch }: UpdateInsurancePolicyArgs) =>
      api.patch<InsurancePolicyResponse>(`/api/compliance/insurance-policies/${id}`, patch),
    onSuccess: () => invalidateColl(qc, 'insurance-policies'),
  });
}

// ---------- Insurance certificates ----------

export function useCreateInsuranceCertificate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateInsuranceCertificateInput) =>
      api.post<InsuranceCertificateResponse>('/api/compliance/insurance-certificates', input),
    onSuccess: () => invalidateColl(qc, 'insurance-certificates'),
  });
}

// ---------- Surety bonds ----------

export function useCreateBond() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateSuretyBondInput) =>
      api.post<SuretyBondResponse>('/api/compliance/bonds', input),
    onSuccess: () => invalidateColl(qc, 'bonds'),
  });
}

export interface UpdateBondArgs extends UpdateSuretyBondInput { id: number }
export function useUpdateBond() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...patch }: UpdateBondArgs) =>
      api.patch<SuretyBondResponse>(`/api/compliance/bonds/${id}`, patch),
    onSuccess: () => invalidateColl(qc, 'bonds'),
  });
}

// ---------- Catalog: license types ----------

export function useCreateLicenseType() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateComplianceLicenseTypeInput) =>
      api.post<ComplianceLicenseTypeResponse>('/api/compliance/license-types', input),
    onSuccess: () => invalidateColl(qc, 'license-types'),
  });
}

export interface UpdateLicenseTypeArgs extends UpdateComplianceLicenseTypeInput { id: number }
export function useUpdateLicenseType() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...patch }: UpdateLicenseTypeArgs) =>
      api.patch<ComplianceLicenseTypeResponse>(`/api/compliance/license-types/${id}`, patch),
    onSuccess: () => invalidateColl(qc, 'license-types'),
  });
}

export function useVerifyLicenseType() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) =>
      api.post<ComplianceLicenseTypeResponse>(`/api/compliance/license-types/${id}/verify`),
    onSuccess: () => invalidateColl(qc, 'license-types'),
  });
}

// ---------- Catalog: requirements ----------

export function useCreateRequirement() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateComplianceRequirementInput) =>
      api.post<ComplianceRequirementResponse>('/api/compliance/requirements', input),
    onSuccess: () => invalidateColl(qc, 'requirements'),
  });
}

export interface UpdateRequirementArgs extends UpdateComplianceRequirementInput { id: number }
export function useUpdateRequirement() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...patch }: UpdateRequirementArgs) =>
      api.patch<ComplianceRequirementResponse>(`/api/compliance/requirements/${id}`, patch),
    onSuccess: () => invalidateColl(qc, 'requirements'),
  });
}

export function useVerifyRequirement() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) =>
      api.post<ComplianceRequirementResponse>(`/api/compliance/requirements/${id}/verify`),
    onSuccess: () => invalidateColl(qc, 'requirements'),
  });
}

// ---------- Catalog: reciprocity ----------

export function useCreateReciprocity() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateReciprocityAgreementInput) =>
      api.post<ReciprocityAgreementResponse>('/api/compliance/reciprocity', input),
    onSuccess: () => invalidateColl(qc, 'reciprocity'),
  });
}

export interface UpdateReciprocityArgs extends UpdateReciprocityAgreementInput { id: number }
export function useUpdateReciprocity() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...patch }: UpdateReciprocityArgs) =>
      api.patch<ReciprocityAgreementResponse>(`/api/compliance/reciprocity/${id}`, patch),
    onSuccess: () => invalidateColl(qc, 'reciprocity'),
  });
}

export function useVerifyReciprocity() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) =>
      api.post<ReciprocityAgreementResponse>(`/api/compliance/reciprocity/${id}/verify`),
    onSuccess: () => invalidateColl(qc, 'reciprocity'),
  });
}

// ---------- Catalog: training certs ----------

export function useCreateTrainingCert() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateComplianceTrainingCertInput) =>
      api.post<ComplianceTrainingCertResponse>('/api/compliance/training-certs', input),
    onSuccess: () => invalidateColl(qc, 'training-certs'),
  });
}

export interface UpdateTrainingCertArgs extends UpdateComplianceTrainingCertInput { id: number }
export function useUpdateTrainingCert() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...patch }: UpdateTrainingCertArgs) =>
      api.patch<ComplianceTrainingCertResponse>(`/api/compliance/training-certs/${id}`, patch),
    onSuccess: () => invalidateColl(qc, 'training-certs'),
  });
}

// ---------- License ↔ policy / bond dependency links (M:N) ----------
// Plain composite-key link rows: POST to add, DELETE to remove. Refresh the
// affected license detail (which projects its linked policies/bonds) and the
// derived analytics.

function invalidateLicenseDetail(
  qc: ReturnType<typeof useQueryClient>,
  licenseId: number,
): void {
  void qc.invalidateQueries({ queryKey: complianceKeys.detail('licenses', licenseId) });
  void qc.invalidateQueries({ queryKey: complianceKeys.gap() });
  void qc.invalidateQueries({ queryKey: complianceKeys.calendar() });
}

export function useLinkLicensePolicy() {
  const qc = useQueryClient();
  return useMutation<unknown, Error, { companyLicenseId: number; insurancePolicyId: number }>({
    mutationFn: ({ companyLicenseId, insurancePolicyId }) =>
      api.post<unknown>(`/api/compliance/licenses/${companyLicenseId}/policies`, {
        policy_id: insurancePolicyId,
      }),
    onSuccess: (_data, { companyLicenseId }) => invalidateLicenseDetail(qc, companyLicenseId),
  });
}

export function useUnlinkLicensePolicy() {
  const qc = useQueryClient();
  return useMutation<void, Error, { companyLicenseId: number; insurancePolicyId: number }>({
    mutationFn: ({ companyLicenseId, insurancePolicyId }) =>
      api.delete<void>(
        `/api/compliance/licenses/${companyLicenseId}/policies/${insurancePolicyId}`,
      ),
    onSuccess: (_data, { companyLicenseId }) => invalidateLicenseDetail(qc, companyLicenseId),
  });
}

export function useLinkLicenseBond() {
  const qc = useQueryClient();
  return useMutation<unknown, Error, { companyLicenseId: number; suretyBondId: number }>({
    mutationFn: ({ companyLicenseId, suretyBondId }) =>
      api.post<unknown>(`/api/compliance/licenses/${companyLicenseId}/bonds`, {
        bond_id: suretyBondId,
      }),
    onSuccess: (_data, { companyLicenseId }) => invalidateLicenseDetail(qc, companyLicenseId),
  });
}

export function useUnlinkLicenseBond() {
  const qc = useQueryClient();
  return useMutation<void, Error, { companyLicenseId: number; suretyBondId: number }>({
    mutationFn: ({ companyLicenseId, suretyBondId }) =>
      api.delete<void>(`/api/compliance/licenses/${companyLicenseId}/bonds/${suretyBondId}`),
    onSuccess: (_data, { companyLicenseId }) => invalidateLicenseDetail(qc, companyLicenseId),
  });
}

// =============================================================================
// Vault document attachment (POST/GET/DELETE /<segment>/:id/document)
// =============================================================================

/** Collection segments that carry a document attachment. */
export type DocumentSegment =
  | 'licenses' | 'insurance-policies' | 'insurance-certificates'
  | 'bonds' | 'training-certs';

export function useUploadComplianceDocument(segment: DocumentSegment) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, file }: { id: number; file: File }) =>
      uploadFile<{ document_path: string }>(url(`${segment}/${id}/document`), file),
    onSuccess: () => invalidateColl(qc, segment),
  });
}

export function useDeleteComplianceDocument(segment: DocumentSegment) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) =>
      api.delete<void>(url(`${segment}/${id}/document`)),
    onSuccess: () => invalidateColl(qc, segment),
  });
}

export function downloadComplianceDocument(segment: DocumentSegment, id: number, documentPath: string): Promise<void> {
  const ext = documentPath.includes('.') ? documentPath.slice(documentPath.lastIndexOf('.')) : '';
  return downloadBlob(url(`${segment}/${id}/document`), `${segment}-${id}${ext}`);
}
